import Alpine from "alpinejs";
import persist from "@alpinejs/persist";
import mitt from "mitt";
import axios from "axios";
import { cloneDeep, debounce, flatMap, isEqual, xor } from "lodash";
import { Loader } from "@googlemaps/js-api-loader";
import {
  getHeaderHeight,
  hasUrlParam,
  getUrlParamValue,
  getAllUrlParamValues,
  removeParamFromUrl,
  createQueryStringFromParams,
  scrollTo,
} from "./utils";

Alpine.plugin(persist);

window.Alpine = Alpine;
window.emitter = mitt();

document.addEventListener(
  "DOMContentLoaded",
  () => {
    /* Add scrollbar-width css variable for accurate calculations */
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${window.innerWidth - document.documentElement.clientWidth}px`
    );

    window.Alpine.store("notification", {
      isOpen: false,
      message: "",
      timeoutId: null,

      setMessage(message) {
        this.message = message;
        this.show();
      },

      show() {
        this.isOpen = true;
        if (this.timeoutId) clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => this.close(), 5000);
      },

      close() {
        clearTimeout(this.timeoutId);
        this.isOpen = false;
        this.message = "";
      },
    });

    window.Alpine.store("searchForm", {
      form: {
        project: "",
        address: window.Alpine.$persist("").as("searchForm_address"),
        lat: window.Alpine.$persist(null).as("searchForm_lat"),
        lng: window.Alpine.$persist(null).as("searchForm_lng"),
        terms: [],
        search: "",
      },
      sliders: {
        area: { min: 0, max: 1000, positions: [0, 100] },
        height: { min: 0, max: 10, positions: [0, 100] },
      },
      order: "relevance",

      init() {
        window.Alpine.nextTick(() => {
          if (this.form.lat && this.form.lng) this.order = "distance";
        });
        window.emitter.on(
          "range-slider:send-value",
          ({ value, handle, positions, type }) => {
            this.sliders[type][handle] = value;
            this.sliders[type]["positions"] = positions;
          }
        );
      },

      resetForm() {
        this.form.project = "";
        this.form.address = "";
        this.form.lat = null;
        this.form.lng = null;
        this.form.terms = [];
        this.form.search = "";
        this.sliders = {
          area: { min: 0, max: 1000, positions: [0, 100] },
          height: { min: 0, max: 10, positions: [0, 100] },
        };
        this.order = "relevance";

        window.emitter.emit("select:set-choice", {
          value: "",
          id: "project-select",
        });
        Object.keys(this.sliders).forEach((key) => {
          window.emitter.emit("range-slider:reset", { type: key });
        });
        window.emitter.emit("search-form:handle-search", { force: true });

        window.history.replaceState(null, null, window.location.pathname);
      },
    });

    window.Alpine.store("favoritePlaces", {
      lists: [],
      placeIds: [],
      currentPlaceId: null,
      originalCurrentPlaceListIds: [],
      currentPlaceListIds: [],
      newListName: "",

      init() {
        this.fetchData();
      },

      get currentPlaceListsChanged() {
        return !isEqual(
          this.originalCurrentPlaceListIds,
          this.currentPlaceListIds
        );
      },

      fetchData() {
        axios({
          url: "/favorites/get",
        }).then((response) => {
          this.lists = response.data;
          this.placeIds = flatMap(response.data, (list) =>
            list.places.map((place) => Number(place.id))
          );
        });
      },

      addToList(listId, message, closeModal = false, modalId = null) {
        const queryUrl = `/favorites/add?place_id=${this.currentPlaceId}&list_id=${listId}`;
        axios({
          url: queryUrl,
        }).then(() => {
          this.fetchData();
          if (closeModal) {
            window.emitter.emit("modal:close", { id: modalId });
          }
          window.Alpine.store("notification").setMessage(message);
        });
      },

      addToNewList(message, closeModal = false, modalId = null) {
        const queryUrl = `/favorites/add-new-list?place_id=${this.currentPlaceId}&list_name=${this.newListName}`;
        axios({
          url: queryUrl,
        }).then(() => {
          this.fetchData();
          this.newListName = "";
          if (closeModal) {
            window.emitter.emit("modal:close", { id: modalId });
            window.emitter.emit("favorites-modal:set-type", { type: "lists" });
          }
          window.Alpine.store("notification").setMessage(message);
        });
      },

      removeFromList(id, listId, message) {
        const queryUrl = `/favorites/remove?place_id=${id}&list_id=${listId}`;
        axios({
          url: queryUrl,
        }).then(() => {
          this.fetchData();
          window.Alpine.store("notification").setMessage(message);
        });
      },

      removeList(listId, message) {
        const queryUrl = `/favorites/removeList/${listId}`;
        axios({
          url: queryUrl,
        }).then(() => {
          this.fetchData();
          window.Alpine.store("notification").setMessage(message);
          window.location.reload();
        });
      },

      updateLists(message, closeModal = false, modalId = null) {
        const params = {
          place_id: this.currentPlaceId,
          list_ids: this.currentPlaceListIds,
        };
        const queryString = createQueryStringFromParams(params);

        axios({
          url: `/favorites/update?${queryString}`,
        }).then(() => {
          this.fetchData();
          if (closeModal) {
            window.emitter.emit("modal:close", { id: modalId });
          }
          window.Alpine.store("notification").setMessage(message);
        });
      },

      setPlaceId(id) {
        this.currentPlaceId = id;
        this.originalCurrentPlaceListIds = this.lists
          .filter((list) =>
            list.places.some(
              (place) => Number(place.id) === this.currentPlaceId
            )
          )
          .map((list) => list.id);
        this.currentPlaceListIds = cloneDeep(this.originalCurrentPlaceListIds);
      },

      toggleFromList(listId) {
        this.currentPlaceListIds = xor(this.currentPlaceListIds, [listId]);
      },
    });

    window.Alpine.start();

    /* Auto Imports */
    const context = require.context("./modules", true, /\.(runtime)\.(.*?)$/);
    context.keys().forEach(context);
  },
  { once: true }
);

window.Alpine.data("upload", () => ({
  fileNames: [],
  inputEl: null,

  init() {
    this.inputEl = this.$el.querySelector('input[type="file"]');
    this.inputEl.addEventListener("change", this.handleChange.bind(this));
  },

  handleChange() {
    this.fileNames = Array.from(this.inputEl.files).map((file) => file.name);
  },
}));

window.Alpine.data("inputSuggestion", ({ type, id, apiPath }) => ({
  isOpen: false,
  currentItems: [],
  selectedItem: null,
  hasQueried: false,
  isFlipped: false,
  flipBalance: 0.5,
  headerHeigth: getHeaderHeight(),
  type,
  id,
  apiPath,

  init() {
    this.$refs.input.value = window.Alpine.store("searchForm").form.search;
    this.$watch("selectedItem", (value) => {
      window.emitter.emit("input-suggestion:send-value", {
        value,
        id: this.id,
      });
    });
  },

  open() {
    this.handleFlipState();
    this.isOpen = true;
  },

  handleFlipState() {
    const rect = this.$root.getBoundingClientRect();
    const spaceAbove = rect.top - this.headerHeigth;
    const spaceBelow = window.innerHeight - rect.bottom;

    if (spaceAbove * this.flipBalance > spaceBelow) this.isFlipped = true;
    else this.isFlipped = false;
  },

  handleFocus($event) {
    if ($event.target.value && this.hasQueried) {
      this.open();
    }
  },

  handleInput($event) {
    this.selectedItem = null;
    window.Alpine.store("searchForm").form.search = $event.target.value;
    if (!$event.target.value) {
      this.isOpen = false;
      this.currentItems = [];
      return;
    }
    const queryUrl = `${this.apiPath}?q=${$event.target.value}`;
    axios({
      url: queryUrl,
    }).then((response) => {
      this.currentItems = response.data;
      if (!this.isOpen) this.open();
      if (!this.hasQueried) this.hasQueried = true;
    });
  },

  handleItemSelection(item) {
    if (this.type === "placeOnly") removeParamFromUrl("search");
    if (item.url) {
      window.location = window.location.origin + item.url;
      return;
    }
    window.Alpine.store("searchForm").form.search = "";
    this.$refs.input.value = item.name;
    this.selectedItem = item;
    this.isOpen = false;
  },
}));

window.Alpine.data("inputRef", ({ apiPath }) => ({
  isOpen: false,
  currentItems: [],
  selectedItem: null,
  hasQueried: false,
  apiPath,

  handleFocus($event) {
    if ($event.target.value && this.hasQueried) {
      this.isOpen = true;
    }
  },

  handleInput($event) {
    this.selectedItem = null;
    if (!$event.target.value) {
      this.isOpen = false;
      this.currentItems = [];
      return;
    }
    const queryUrl = `${this.apiPath}?q=${$event.target.value}`;
    axios({
      url: queryUrl,
    }).then((response) => {
      this.currentItems = response.data.filter((item) => item.type === "place");
      if (!this.isOpen) this.isOpen = true;
      if (!this.hasQueried) this.hasQueried = true;
    });
  },

  handleItemSelection(item) {
    window.location = window.location.origin + item.url;
  },
}));

window.Alpine.data("inputAddress", ({ id }) => ({
  loader: null,
  id,

  init() {
    this.loader = new Loader({
      apiKey: "AIzaSyB_m_5qtr5TAhfR-H_xn4aL5u5gvvGgecc",
      version: "weekly",
      libraries: ["places"],
    });

    this.loader.load().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        this.$refs.input,
        {
          fields: ["geometry"],
          types: ["geocode"],
        }
      );
      autocomplete.addListener("place_changed", () => {
        const placeInfos = autocomplete.getPlace();
        window.Alpine.store("searchForm").form.address = this.$refs.input.value;
        window.Alpine.store("searchForm").form.lat =
          placeInfos.geometry.location.lat();
        window.Alpine.store("searchForm").form.lng =
          placeInfos.geometry.location.lng();
        window.Alpine.store("searchForm").order = "distance";
      });
      // Removing address when the field is cleared
      this.$refs.input.addEventListener("change", (e) => {
        if (!e.target.value) {
          window.Alpine.store("searchForm").form.address = "";
          window.Alpine.store("searchForm").form.lat = null;
          window.Alpine.store("searchForm").form.lng = null;
          window.Alpine.store("searchForm").order = "relevance";
        }
      });
    });
  },
}));

window.Alpine.data("header", () => ({
  isMobileMenuOpen: false,
  isLocaleDropdownVisible: false,
  isAccountDropdownVisible: false,

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  },

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  },

  toggleLocaleDropdown() {
    this.isLocaleDropdownVisible = !this.isLocaleDropdownVisible;
  },

  toggleAccountDropdown() {
    this.isAccountDropdownVisible = !this.isAccountDropdownVisible;
  },
}));

window.Alpine.data("sliderGrid", () => ({
  setGalleryItem(itemId, galleryId) {
    window.emitter.emit("gallery:set-item", { itemId, galleryId });
  },
}));

window.Alpine.data("gallery", ({ items, id, sliderId, navSliderId }) => ({
  currentId: null,
  lastNavIndex: 0,
  display: "list",
  items,
  id,
  sliderId,
  navSliderId,

  init() {
    this.currentId = Number(items[0].id);
    window.emitter.on("gallery:set-item", ({ itemId, galleryId }) => {
      if (this.id === galleryId) this.switchItem(itemId);
    });
    window.emitter.on("gallery:set-slide-index", ({ index }) => {
      this.currentId = this.findCurrentItemIdFromIndex(index);
    });
    window.emitter.on("gallery:focus-nav", () => {
      if (this.lastNavIndex === this.activeItem.index) return;
      this.lastNavIndex = this.activeItem.index;
      window.emitter.emit("slider:slide-to", {
        id: this.navSliderId,
        index: this.activeItem.index,
        speed: 300,
      });
    });
  },

  get updatedItems() {
    let slideIndex = 0;
    return this.items.map((item, itemIndex) => {
      const data = {
        ...item,
        id: Number(item.id),
        index: itemIndex,
        firstSlideIndex: slideIndex,
        images: item.images.map((image, imageIndex) => ({
          ...image,
          index: slideIndex + imageIndex,
        })),
      };
      slideIndex = slideIndex + item.images.length;
      return data;
    });
  },

  get activeItem() {
    return this.updatedItems.find((item) => item.id === this.currentId);
  },

  findCurrentItemIdFromIndex(index) {
    const firstSlideIndexes = this.updatedItems.map(
      (item) => item.firstSlideIndex
    );
    const output = firstSlideIndexes.reduce((prev, curr) => {
      return index - curr < 0 ? prev : curr;
    });
    return this.updatedItems.find((item) => item.firstSlideIndex === output).id;
  },

  switchItem(id) {
    this.currentId = id;
    const slideIndex = this.updatedItems.find(
      (item) => item.id === this.currentId
    ).firstSlideIndex;
    window.emitter.emit("slider:slide-to", {
      id: this.sliderId,
      index: slideIndex,
      speed: 0,
    });
  },

  switchDisplay(display) {
    this.display = display;
  },

  switchToCurrentSlide(slideIndex) {
    window.emitter.emit("slider:slide-to", {
      id: this.sliderId,
      index: slideIndex,
      speed: 0,
    });
    this.display = "slider";
  },
}));

window.Alpine.data("customSelect", ({ items, categories, id }) => ({
  isOpen: false,
  currentItems: null,
  selectedItemValues: [],
  selectedCategory: null,
  selectedParent: null,
  items,
  categories,
  id,

  init() {
    if (!(this.categories && this.categories.length > 0))
      this.setCurrentItems();
  },

  toggleVisibility() {
    this.isOpen = !this.isOpen;
  },

  close() {
    this.isOpen = false;
  },

  handleItemSelection(item) {
    if (item.children && item.children.length > 0) {
      this.selectedParent = item;
      this.currentItems = item.children;
      return;
    }
    const index = this.selectedItemValues.indexOf(item.value);
    if (index > -1) this.selectedItemValues.splice(index, 1);
    else this.selectedItemValues.push(item.value);
  },

  handleCategorySelection(category) {
    this.selectedCategory = category;
    this.setCurrentItems();
  },

  setCurrentItems() {
    this.currentItems = this.selectedCategory
      ? this.items.filter(
          (item) => item.categoryId === this.selectedCategory.id
        )
      : this.items;
  },

  backToCategories() {
    this.selectedCategory = null;
    this.selectedParent = null;
    this.currentItems = null;
  },

  backToPreviousLevel() {
    const currentLevel = this.selectedParent.level;
    if (currentLevel === 0) {
      this.selectedParent = null;
      this.setCurrentItems();
    } else {
      const itemsFromPreviousLevel = this.items.filter(
        (item) => item.level === currentLevel - 1
      );
      this.selectedParent = itemsFromPreviousLevel.find((item) => {
        return (
          item.children &&
          item.children.some(
            (child) => child.value === this.selectedParent.value
          )
        );
      });
      this.currentItems = this.selectedParent.children;
    }
  },

  isItemSelected(item) {
    if (item.children && item.children.length > 0)
      return item.children.some((child) => this.isItemSelected(child));
    return this.selectedItemValues.indexOf(item.value) > -1;
  },
}));

window.Alpine.data("customSelectMultiple", ({ filters, id }) => ({
  openId: null,
  filters,
  id,

  init() {
    const ids = this.filters.map((item) => item.id);
    window.emitter.on("custom-select:toggle-visibility", ({ id }) => {
      if (ids.indexOf(id) === -1) return;
      if (this.openId === id) this.openId = null;
      else this.openId = id;
    });
    window.emitter.on("custom-select:close", ({ id }) => {
      if (ids.indexOf(id) === -1) return;
      if (this.openId === id) this.openId = null;
    });
  },
}));

window.Alpine.data("customSelectHead", ({ id }) => ({
  items: [],
  id,

  init() {
    this.items = this.filters.find((filter) => filter.id === this.id).items;
  },

  get isOpen() {
    return this.id === this.openId;
  },

  get itemsCount() {
    let count = 0;
    window.Alpine.store("searchForm").form.terms.forEach((term) => {
      if (this.isTermSelected(term, this.items)) count = count + 1;
    });
    return count;
  },

  isTermSelected(term, items) {
    return items.some((item) => {
      if (item.value === term) return true;
      if (item.children && item.children.length > 0)
        return this.isTermSelected(term, item.children);
      return false;
    });
  },

  toggleVisibility() {
    window.emitter.emit("custom-select:toggle-visibility", { id: this.id });
  },
}));

window.Alpine.data("customSelectBody", ({ categories, id }) => ({
  currentItems: null,
  selectedCategory: null,
  selectedParent: null,
  items: [],
  categories,
  id,

  init() {
    this.items = this.filters.find((filter) => filter.id === this.id).items;
    if (!(this.categories && this.categories.length > 0))
      this.setCurrentItems();
  },

  get isOpen() {
    return this.id === this.openId;
  },

  close() {
    window.emitter.emit("custom-select:close", { id: this.id });
  },

  deeplySelectItem(item) {
    const index = window.Alpine.store("searchForm").form.terms.indexOf(
      item.value
    );
    if (index === -1)
      window.Alpine.store("searchForm").form.terms.push(item.value);
    if (this.isParent(item)) {
      item.children.forEach((child) => this.deeplySelectItem(child));
    }
  },

  deeplyRemoveItem(item) {
    const index = window.Alpine.store("searchForm").form.terms.indexOf(
      item.value
    );
    if (index > -1)
      window.Alpine.store("searchForm").form.terms.splice(index, 1);
    if (this.isParent(item)) {
      item.children.forEach((child) => this.deeplyRemoveItem(child));
    }
  },

  handleItemSelection(item) {
    const index = window.Alpine.store("searchForm").form.terms.indexOf(
      item.value
    );
    if (this.isParent(item)) {
      this.showChildren(item);
      return;
    }
    if (index > -1) this.deeplyRemoveItem(item);
    else this.deeplySelectItem(item);
  },

  showChildren(item) {
    const clonedItem = cloneDeep(item);
    delete clonedItem.children;
    delete clonedItem.categoryId;
    this.selectedParent = item;
    this.currentItems = [clonedItem, ...item.children];
    scrollTo(this.$root);
  },

  handleCategorySelection(category) {
    this.selectedCategory = category;
    this.setCurrentItems();
  },

  setCurrentItems() {
    this.currentItems = this.selectedCategory
      ? this.items.filter(
          (item) => item.categoryId === this.selectedCategory.id
        )
      : this.items;
  },

  backToCategories() {
    this.selectedCategory = null;
    this.selectedParent = null;
    this.currentItems = null;
  },

  backToPreviousLevel() {
    const currentLevel = this.selectedParent.level;
    if (currentLevel === 0) {
      this.selectedParent = null;
      this.setCurrentItems();
    } else {
      const itemsFromPreviousLevel = this.items.filter(
        (item) => item.level === currentLevel - 1
      );
      this.selectedParent = itemsFromPreviousLevel.find((item) => {
        return (
          item.children &&
          item.children.some(
            (child) => child.value === this.selectedParent.value
          )
        );
      });
      this.currentItems = this.selectedParent.children;
    }
  },

  isParent(item) {
    return item.children && item.children.length > 0;
  },

  isItemSelected(item) {
    return (
      window.Alpine.store("searchForm").form.terms.indexOf(item.value) > -1
    );
  },

  hasChildrenSelected(item) {
    if (!this.isParent(item)) return false;
    return item.children.some((child) => {
      return (
        this.hasChildrenSelected(child) ||
        window.Alpine.store("searchForm").form.terms.indexOf(child.value) >
          -1 ||
        this.isItemSelected(item)
      );
    });
  },
}));

window.Alpine.data("customSelectBodyCategory", ({ id }) => ({
  id,

  get categoryItemsCount() {
    let count = 0;
    window.Alpine.store("searchForm").form.terms.forEach((term) => {
      if (this.isTermSelectedInCategory(term, this.items, this.id))
        count = count + 1;
    });
    return count;
  },

  isTermSelectedInCategory(term, items, categoryId = null) {
    return items.some((item) => {
      if (categoryId && item.categoryId !== categoryId) return false;
      if (item.value === term) return true;
      if (item.children && item.children.length > 0)
        return this.isTermSelectedInCategory(term, item.children);
      return false;
    });
  },
}));

window.Alpine.data("searchEngineSimple", ({ id, path }) => ({
  id,
  path,

  init() {
    window.emitter.on("input-suggestion:send-value", ({ value, id }) => {
      if (this.id !== id) return;
      if (value) window.Alpine.store("searchForm").form.terms = [value.id];
      else window.Alpine.store("searchForm").form.terms = [];
    });
  },

  handleSearch(advanced = false) {
    if (!window.Alpine.store("searchForm").form.project && !advanced) return;

    const params = {
      ...window.Alpine.store("searchForm").form,
      order: window.Alpine.store("searchForm").order,
    };
    const queryString = createQueryStringFromParams(params);

    window.location = `${this.path}?${queryString}`;
  },
}));

window.Alpine.data("pagePlaceIndexForm", ({ id }) => ({
  isLoading: false,
  lastState: null,
  showAllFilters: false,
  id,

  init() {
    if (hasUrlParam("project")) {
      window.Alpine.store("searchForm").form.project =
        getUrlParamValue("project");
    }
    if (hasUrlParam("search")) {
      window.Alpine.store("searchForm").form.search =
        getUrlParamValue("search");
    }
    if (hasUrlParam("terms[]")) {
      window.Alpine.store("searchForm").form.terms =
        getAllUrlParamValues("terms[]");
      this.showAllFilters = true;
    }
    if (hasUrlParam("min_area"))
      window.Alpine.store("searchForm").sliders.area.min = Number(
        getUrlParamValue("min_area")
      );
    if (hasUrlParam("max_area"))
      window.Alpine.store("searchForm").sliders.area.max = Number(
        getUrlParamValue("max_area")
      );
    if (hasUrlParam("min_height"))
      window.Alpine.store("searchForm").sliders.height.min = Number(
        getUrlParamValue("min_height")
      );
    if (hasUrlParam("max_height"))
      window.Alpine.store("searchForm").sliders.height.max = Number(
        getUrlParamValue("max_height")
      );
    this.setLastState();
    window.emitter.on("search-form:handle-search", ({ force }) => {
      this.handleSearch(force);
    });
  },

  get hasValues() {
    return (
      Object.values(window.Alpine.store("searchForm").form).some((value) =>
        Array.isArray(value) ? value.length > 0 : value
      ) || this.hasSliderValues
    );
  },

  get hasSliderValues() {
    return Object.values(window.Alpine.store("searchForm").sliders).some(
      (slider) => JSON.stringify(slider.positions) !== "[0,100]"
    );
  },

  get hasUpdate() {
    return (
      JSON.stringify({
        ...window.Alpine.store("searchForm").form,
        ...window.Alpine.store("searchForm").sliders,
      }) !== JSON.stringify(this.lastState)
    );
  },

  setLastState() {
    this.lastState = cloneDeep({
      ...window.Alpine.store("searchForm").form,
      ...window.Alpine.store("searchForm").sliders,
    });
  },

  handleSearch(force = false) {
    if (!window.Alpine.store("searchForm").form.project && !force) return;

    this.setLastState();
    this.isLoading = true;

    const params = {
      ...window.Alpine.store("searchForm").form,
      min_area: window.Alpine.store("searchForm").sliders.area.min,
      max_area: window.Alpine.store("searchForm").sliders.area.max,
      min_height: window.Alpine.store("searchForm").sliders.height.min,
      max_height: window.Alpine.store("searchForm").sliders.height.max,
      order: window.Alpine.store("searchForm").order,
    };
    const queryString = createQueryStringFromParams(params);

    window.history.replaceState(
      null,
      null,
      `${window.location.pathname}?${queryString}`
    );

    axios({
      url: `?${queryString}`,
      responseType: "document",
    }).then((response) => {
      const resultsHTML = response.data.querySelector(
        '[data-ref="results-wrapper"]'
      ).innerHTML;
      document.querySelector('[data-ref="results-wrapper"]').innerHTML =
        resultsHTML;
      this.isLoading = false;
    });
  },
}));

window.Alpine.data("pagePlaceIndexSorting", () => ({
  handleChange($event) {
    window.Alpine.store("searchForm").order = $event.target.value;
    window.emitter.emit("search-form:handle-search", { force: true });
  },
}));

window.Alpine.data("pageIndexPager", () => ({
  isLoading: false,

  loadMore() {
    this.isLoading = true;
    const nextUrl = this.$root
      .querySelector(".pager__item.pager__item--next a")
      .getAttribute("href");
    axios({
      url: nextUrl,
      responseType: "document",
    }).then((response) => {
      const placeItemsHTML = response.data.querySelector(
        '[data-ref="index-grid-items"]'
      ).innerHTML;
      const pagerHTML = response.data.querySelector(
        '[data-ref="original-pager"]'
      ).innerHTML;
      document
        .querySelector('[data-ref="index-grid-items"]')
        .insertAdjacentHTML("beforeend", placeItemsHTML);
      this.$root.querySelector('[data-ref="original-pager"]').innerHTML =
        pagerHTML;
      const nextEl = this.$root.querySelector(".pager__item.pager__item--next");
      if (!nextEl) this.$root.style.display = "none";
      this.isLoading = false;
    });
  },
}));

window.Alpine.data("pagePlaceAside", () => ({
  ticking: false,
  isFixed: false,
  elOffset: null,
  headerHeigth: getHeaderHeight(),
  spaceFromHeader: 32,
  cardEl: null,

  setLayout() {
    const { elOffset, headerHeigth, spaceFromHeader, cardEl, $el } = this;
    // Scroll position enters in fixed mode
    if (
      !this.isFixed &&
      $el.scrollTop + headerHeigth + spaceFromHeader > elOffset.top &&
      $el.scrollTop + headerHeigth + spaceFromHeader <
        elOffset.bottom - cardEl.offsetHeight
    ) {
      this.setCardPosition(
        "fixed",
        `${headerHeigth + spaceFromHeader}px`,
        `${document.documentElement.clientWidth - elOffset.right}px`,
        "auto",
        `${elOffset.left}px`
      );
      this.isFixed = true;
    }
    // Scroll position enters above fixed zone
    if (
      this.isFixed &&
      $el.scrollTop + headerHeigth + spaceFromHeader <= elOffset.top
    ) {
      this.setCardPosition("absolute", "0", "0", "auto", "0");
      this.isFixed = false;
    }
    // Scroll position enters below fixed zone
    if (
      this.isFixed &&
      $el.scrollTop + headerHeigth + spaceFromHeader >=
        elOffset.bottom - cardEl.offsetHeight
    ) {
      this.setCardPosition("absolute", "auto", "0", "0", "0");
      this.isFixed = false;
    }
  },

  setCardPosition(type, top, right, bottom, left) {
    const { cardEl } = this;
    cardEl.style.position = type;
    cardEl.style.top = top;
    cardEl.style.right = right;
    cardEl.style.bottom = bottom;
    cardEl.style.left = left;
  },

  setCardPositionOnResize() {
    this.elOffset = this.$el.getBoundingClientRect();
    if (this.isFixed && window.innerWidth >= 960) {
      this.cardEl.style.right = `${
        document.documentElement.clientWidth - this.elOffset.right
      }px`;
      this.cardEl.style.left = `${this.elOffset.left}px`;
    }
  },

  init() {
    this.cardEl = this.$refs["page-place-aside-card"];
    window.addEventListener("scroll", () => {
      this.elOffset = this.$el.getBoundingClientRect();
      if (window.innerWidth < 960) return;
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.setLayout();
          this.ticking = false;
        });
      }
      this.ticking = true;
    });
    window.addEventListener(
      "resize",
      debounce(this.setCardPositionOnResize.bind(this), 100)
    );
  },
}));

window.Alpine.data("pageDashboard", ({ tabs }) => ({
  activeTab: "favorite-places",
  selectedListId: null,
  tabs,

  init() {
    if (window.location.hash) {
      const slug = window.location.hash.replace("#", "");
      if (this.tabs.some((item) => item.slug === slug)) {
        this.activeTab = slug;
      }
    }
  },

  get tabInfo() {
    return this.tabs.find((item) => item.slug === this.activeTab);
  },

  setActiveTab(slug) {
    this.activeTab = slug;
    window.location.hash = `#${slug}`;
  },

  setFavoritePlacesList($event) {
    this.selectedListId = $event.detail.value
      ? Number($event.detail.value)
      : null;
    window.emitter.emit("list-contact-form-modal:set-list-id", {
      id: this.selectedListId,
    });
  },

  switchToContactForm(isSwitched = false) {
    this.$dispatch("shared-forms-modal:set-type", "contact");
    this.$dispatch("contact-form:set-switched", isSwitched);
  },
}));

window.Alpine.data("sharedFormsModal", ({ formsData }) => ({
  formType: "",
  formsData,

  init() {
    const modalParamValue = getUrlParamValue("modal");
    if (modalParamValue?.startsWith('contact')) {
      this.openContactModal(modalParamValue);
    }
  },

  get title() {
    return this.formsData[this.formType]?.title || "";
  },

  openContactModal(type) {
    setTimeout(() => {
      window.emitter.emit("modal:open", {
        id: 'modal-shared-forms',
      });
      this.switchToContactForm(type === 'contact-suggest');
    }, 500);
  },

  switchToContactForm(isSwitched = false) {
    this.formType = "contact";
    this.$dispatch("contact-form:set-switched", isSwitched);
  },
}));

window.Alpine.data("favoritesModal", ({ modalData }) => ({
  modalType: "lists",
  modalData,

  get title() {
    return this.modalData[this.modalType]?.title || "";
  },

  init() {
    window.emitter.on("favorites-modal:set-type", ({ type }) => {
      this.switchType(type);
    });
  },

  switchType(type) {
    this.modalType = type;
  },
}));

window.Alpine.data("listContactFormModal", () => ({
  selectedListId: null,

  init() {
    window.emitter.on("list-contact-form-modal:set-list-id", ({ id }) => {
      this.selectedListId = id;
      this.$root.querySelector('[data-drupal-selector="edit-list-id"]').value =
        id;
    });
  },

  get currentList() {
    return window.Alpine.store("favoritePlaces").lists.find(
      (list) => Number(list.id) === this.selectedListId
    );
  },
}));
